import { useState, useEffect } from 'react';
import { absencesAPI, usersAPI, getErrorMessage } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  UserX, 
  Plus, 
  Edit2, 
  Trash2, 
  Loader2,
  Calendar,
  CheckCircle2,
  XCircle,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function Absences() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [filters, setFilters] = useState({
    start_date: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    user_id: 'all'
  });
  const [formData, setFormData] = useState({
    user_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    absence_type: 'no_show',
    reason: '',
    is_excused: false
  });

  // Absence types with translations
  const ABSENCE_TYPES = [
    { value: 'no_show', labelKey: 'absences.type.noShow', color: 'text-rose-400' },
    { value: 'sick', labelKey: 'absences.type.sick', color: 'text-amber-400' },
    { value: 'vacation', labelKey: 'absences.type.vacation', color: 'text-blue-400' },
    { value: 'personal', labelKey: 'absences.type.personal', color: 'text-violet-400' },
    { value: 'other', labelKey: 'absences.type.other', color: 'text-gray-400' },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const apiFilters = { ...filters };
      if (apiFilters.user_id === 'all') {
        delete apiFilters.user_id;
      }
      const [absencesRes, employeesRes] = await Promise.all([
        absencesAPI.list(apiFilters),
        usersAPI.list({ role: 'employee' })
      ]);
      setAbsences(absencesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      absence_type: 'no_show',
      reason: '',
      is_excused: false
    });
    setEditingAbsence(null);
  };

  const openEditDialog = (absence) => {
    setEditingAbsence(absence);
    setFormData({
      user_id: absence.user_id,
      date: absence.date,
      absence_type: absence.absence_type,
      reason: absence.reason || '',
      is_excused: absence.is_excused
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAbsence) {
        await absencesAPI.update(editingAbsence.id, {
          absence_type: formData.absence_type,
          reason: formData.reason || null,
          is_excused: formData.is_excused
        });
        toast.success(t('absences.msg.updated'));
      } else {
        await absencesAPI.create(formData);
        toast.success(t('absences.msg.added'));
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(getErrorMessage(error, t('msg.error')));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('absences.confirmDelete'))) return;
    
    try {
      await absencesAPI.delete(id);
      toast.success(t('absences.msg.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const getAbsenceTypeInfo = (type) => {
    return ABSENCE_TYPES.find(t => t.value === type) || ABSENCE_TYPES[4];
  };

  return (
    <div className="space-y-6" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
            <UserX className="h-8 w-8 text-rose-400" />
            {t('absences.title')}
          </h1>
          <p className="text-wp-light/60 mt-1">{t('absences.subtitle')}</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="w-full md:w-auto bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-wp-medium hover:to-violet-500"
          data-testid="add-absence-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('common.add')}
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl p-4 md:p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-wp-light/50" />
            <span className="text-wp-light/70">{t('common.filters')}:</span>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Label className="text-wp-light/70">{t('common.from')}:</Label>
            <Input
              type="date"
              lang={language}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              className="w-full md:w-auto bg-wp-dark/30 border-wp-dark/50 text-white"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Label className="text-wp-light/70">{t('common.to')}:</Label>
            <Input
              type="date"
              lang={language}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              className="w-full md:w-auto bg-wp-dark/30 border-wp-dark/50 text-white"
            />
          </div>
          <Select value={filters.user_id} onValueChange={(v) => setFilters({...filters, user_id: v})}>
            <SelectTrigger className="w-full md:w-[200px] bg-wp-dark/30 border-wp-dark/50 text-white">
              <SelectValue placeholder={t('absences.allEmployees')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('absences.allEmployees')}</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-wp-light" />
          </div>
        ) : absences.length === 0 ? (
          <div className="text-center py-12 text-wp-light/40">
            <UserX className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('absences.noRecords')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-wp-dark/50 hover:bg-wp-dark/30">
                <TableHead className="text-wp-light/70">{t('common.date')}</TableHead>
                <TableHead className="text-wp-light/70">{t('absences.employee')}</TableHead>
                <TableHead className="text-wp-light/70">{t('absences.type')}</TableHead>
                <TableHead className="text-wp-light/70">{t('absences.reason')}</TableHead>
                <TableHead className="text-wp-light/70">{t('common.status')}</TableHead>
                <TableHead className="text-wp-light/70">{t('absences.modifiedBy')}</TableHead>
                <TableHead className="text-right text-wp-light/70">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {absences.map((absence) => {
                const typeInfo = getAbsenceTypeInfo(absence.absence_type);
                return (
                  <TableRow 
                    key={absence.id} 
                    className="border-wp-dark/50 hover:bg-wp-dark/30"
                    data-testid={`absence-row-${absence.id}`}
                  >
                    <TableCell className="text-white font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-wp-light/50" />
                        {format(new Date(absence.date), 'dd.MM.yyyy')}
                      </div>
                    </TableCell>
                    <TableCell className="text-white">{absence.user_name}</TableCell>
                    <TableCell>
                      <span className={`${typeInfo.color} font-medium`}>
                        {t(typeInfo.labelKey)}
                      </span>
                    </TableCell>
                    <TableCell className="text-wp-light/60 max-w-[200px] truncate">
                      {absence.reason || '-'}
                    </TableCell>
                    <TableCell>
                      {absence.is_excused ? (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <CheckCircle2 className="h-4 w-4" />
                          {t('absences.excused')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-rose-400">
                          <XCircle className="h-4 w-4" />
                          {t('absences.unexcused')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-wp-light/40 text-sm">
                      {absence.updated_by_name || absence.created_by_name || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => openEditDialog(absence)}
                          className="p-2 rounded-lg bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/50 hover:text-wp-lightest transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(absence.id)}
                          className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-wp-darker border-wp-dark/50">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingAbsence ? t('absences.editRecord') : t('absences.addAbsence')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingAbsence && (
              <>
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('absences.employee')}</Label>
                  <Select value={formData.user_id} onValueChange={(v) => setFormData({...formData, user_id: v})}>
                    <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                      <SelectValue placeholder={t('absences.selectEmployee')} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('common.date')}</Label>
                  <Input
                    type="date"
                    lang={language}
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    className="bg-wp-dark/30 border-wp-dark/50 text-white"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('absences.absenceType')}</Label>
              <Select value={formData.absence_type} onValueChange={(v) => setFormData({...formData, absence_type: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className={type.color}>{t(type.labelKey)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('absences.reason')}</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder={t('absences.reasonPlaceholder')}
                className="bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-xl bg-wp-dark/30">
              <Label className="text-white">{t('absences.excusedReason')}</Label>
              <Switch
                checked={formData.is_excused}
                onCheckedChange={(v) => setFormData({...formData, is_excused: v})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="flex-1 text-wp-light/70 hover:text-wp-lightest"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!editingAbsence && !formData.user_id}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600"
              >
                {editingAbsence ? t('common.save') : t('common.add')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
